import React, { useEffect, useEffectEvent, useState } from 'react';

import {
  defaultInitialsFieldWidthPx,
  defaultSignatureFieldWidthPx,
  getIsInitialsSignatureField,
  initialsFieldAspectRatio,
  initialsPlaceholderLayoutPx,
  minInitialsFieldWidthPx,
  minSignatureFieldWidthPx,
  signatureFieldAspectRatio,
} from '@aline/common';
import type { DrawReactComponentProps } from '@aline/common';
import { makeVar } from '@apollo/client';
import { clamp } from 'ramda';
import { Queries } from 'src/editor/queries';
import { useAuthorizedTransforms, useGetEditorUserState } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { SignatureEmptyState } from 'src/layout-engine/components/SignatureEmptyState';
import { useEditingDisabled } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

export const resizingSignatureFieldIdVar = makeVar<string | null>(null);

const HANDLE_SIZE_PX = initialsPlaceholderLayoutPx.handleSize;

const useStyles = makeStyles()(theme => ({
  resizeHandle: {
    backgroundColor: theme.palette.primary.main,
    border: '1px solid white',
    borderRadius: '50%',
    bottom: -HANDLE_SIZE_PX / 2,
    boxSizing: 'border-box',
    cursor: 'nwse-resize',
    height: HANDLE_SIZE_PX,
    pointerEvents: 'auto',
    position: 'absolute',
    right: -HANDLE_SIZE_PX / 2,
    width: HANDLE_SIZE_PX,
    zIndex: 1,
  },
  root: {
    cursor: 'pointer',
    position: 'absolute',
  },
}));

interface DragState {
  startClientX: number;
  startWidth: number;
}

export function SignatureFieldPlaceholder({
  isSelected,
  signatureFieldEntry,
}: DrawReactComponentProps['SignatureFieldPlaceholder']) {
  const [signatureField, cMPath] = signatureFieldEntry;
  const editor = useEditor();
  const editingDisabled = useEditingDisabled();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { isInSigningCeremony } = useGetEditorUserState();

  const isInitials = getIsInitialsSignatureField(signatureField);
  const aspectRatio = isInitials ? initialsFieldAspectRatio : signatureFieldAspectRatio;
  const defaultWidth = isInitials ? defaultInitialsFieldWidthPx : defaultSignatureFieldWidthPx;
  const minWidth = isInitials ? minInitialsFieldWidthPx : minSignatureFieldWidthPx;

  const { imgHeightPx: storedHeight, imgWidthPx: storedWidth } =
    Queries.getSignatureImageDimensions(signatureField);

  const [drag, setDrag] = useState<DragState | null>(null);
  const [preview, setPreview] = useState<{
    height: number;
    width: number;
  } | null>(null);

  const { classes } = useStyles();

  function clampWidth(rawWidth: number): number {
    return clamp(minWidth, defaultWidth, rawWidth);
  }

  function handleMouseDown(event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    setDrag({ startClientX: event.clientX, startWidth: storedWidth });
    setPreview({ height: storedHeight, width: storedWidth });
    resizingSignatureFieldIdVar(signatureField.fieldId);
  }

  const handleMouseMove = useEffectEvent((event: MouseEvent) => {
    if (!drag) return;
    const newWidth = clampWidth(drag.startWidth + (event.clientX - drag.startClientX));
    setPreview({ height: newWidth / aspectRatio, width: newWidth });
  });

  const handleMouseUp = useEffectEvent((event: MouseEvent) => {
    if (!drag) return;
    const finalWidth = clampWidth(drag.startWidth + (event.clientX - drag.startClientX));
    // A click without a drag leaves the field alone, even when its size is outside today's range.
    const hasMoved = event.clientX !== drag.startClientX;
    if (hasMoved && finalWidth !== drag.startWidth) {
      AuthorizedTransforms.setSigFieldProps(editor, {
        at: cMPath,
        props: {
          imgHeightPx: finalWidth / aspectRatio,
          imgWidthPx: finalWidth,
        },
      });
    }
    setDrag(null);
    setPreview(null);
    resizingSignatureFieldIdVar(null);
  });

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);

      if (resizingSignatureFieldIdVar() === signatureField.fieldId)
        resizingSignatureFieldIdVar(null);
    };
  }, [signatureField.fieldId]);

  const { height, width } = preview ?? {
    height: storedHeight,
    width: storedWidth,
  };

  return (
    <div className={classes.root} style={{ height, width }}>
      <SignatureEmptyState
        isInitials={isInitials}
        isSelected={isSelected}
        isSigning={isInSigningCeremony}
        scale={width / defaultWidth}
      />
      {!editingDisabled && <div className={classes.resizeHandle} onMouseDown={handleMouseDown} />}
    </div>
  );
}
